/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */
import {formatMoney} from '@shopify/theme-currency';
import {imageUrl} from '../scripts/utils';
import {load} from '@shopify/theme-sections';

import '../scripts/sections/tagalys-smart-widget';
import '../scripts/sections/tagalys-recommendations';
import '../scripts/sections/tagalys-bought-also-bought';
import '../scripts/sections/tagalys-viewed-also-viewed';

import MatchHeight from 'matchheight';
import {initLoyalty} from '../scripts/loyalty';
import {commaSeparateWithAnd} from '../scripts/utils';
import searchSuggestionsTagalys from '../scripts/search/search-suggestions-tagalys';
import configureTagalys from '../scripts/search/configure-tagalys';
import 'lazyvids';
import {initGiftWithPurchase} from '../scripts/gift-with-purchase';
import {initGiftCardLanding} from '../scripts/gift-card-landing';
import {initPostcodeSearch} from '../scripts/components/postcode-search';
import initPickupAvailability from '../scripts/components/pickup-availability';

/**
 * Postcode Search
 */
document.addEventListener('DOMContentLoaded', function () {
  initPostcodeSearch();
  initPickupAvailability();
});

/**
 * Remove Zip Widget's Title
 * - The zip title on older versions of the widget outputs a <title> tag
 *   that is causing SEO issues (multiple title tags on the page)
 */
document.addEventListener('DOMContentLoaded', function () {
  const zip = document.querySelector('#zip-prod-widget');
  if (!zip) return;

  const removeZipTitle = () => {
    const title = zip.querySelector('title');
    if (title) zip.removeChild(title);
  };

  const hasLoadedZipWidget = () => {
    return zip.dataset?.zmRendered == 'true';
  };

  const checkZipWidgetHasLoaded = () => {
    if (!hasLoadedZipWidget()) return;
    clearInterval(zipLoadedInterval);
    removeZipTitle(zip);
  };

  const zipLoadedInterval = setInterval(checkZipWidgetHasLoaded, 300);
});

/**
 * Promos
 */
document.addEventListener('DOMContentLoaded', function () {
  document.body.addEventListener('click', function (event) {
    const promoAddToCart = event.target.closest('[data-add-promo-to-cart]');
    if (!promoAddToCart) return;

    event.preventDefault();
    event.stopPropagation();

    const items = JSON.parse(promoAddToCart.dataset.addPromoToCart);
    const data = {
      items: items,
    };
    const url = '/cart/add.js';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };

    fetch(url, options)
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        if (window.location.pathname === '/cart') {
          window.location.reload();
        } else {
          window.location = '/cart';
        }
      })
      .catch(function (error) {
        window.console.error(error);
      });
  });
});

/**
 * Product shipping message links
 */
document.addEventListener('DOMContentLoaded', function () {
  const shippingMessageLinks = document.querySelectorAll('.Product__ShippingLabel a[href^="#"]');
  shippingMessageLinks.forEach(function (shippingMessageLink) {
    shippingMessageLink.addEventListener('click', function (event) {
      const panel = document.querySelector(event.currentTarget.getAttribute('href'));
      if (!panel) return;

      const panelToggle = panel.querySelector('[data-action="toggle-collapsible"][aria-expanded="false"]');
      if (!panelToggle) return;

      panelToggle.click();
    });
  });
});

/**
 * Get pickup location availablity from shipping methods on the PDP
 * This is used for both the main product on the page, and any GWP
 * products promoted on this page.
 */
document.addEventListener('DOMContentLoaded', async () => {
  const PICKUP_PREFIX = 'Pickup from ';

  const AU_PICKUP_LOCATIONS = Object.entries(window.theme?.clickAndCollectAddresses)
    .filter(([locationName, locationDetails]) => locationDetails.country === 'AU')
    .map(([locationName]) => locationName);
  const NZ_PICKUP_LOCATIONS = Object.entries(window.theme?.clickAndCollectAddresses)
    .filter(([locationName, locationDetails]) => locationDetails.country === 'NZ')
    .map(([locationName]) => locationName);

  let cartCookie;

  const pickupLocationsLists = document.querySelectorAll('[data-pickup-locations-list]');
  for (const pickupLocationsList of pickupLocationsLists) {
    const productAvailability = pickupLocationsList.closest('[data-product-availability]');
    const pickupAvailability = productAvailability
      ? productAvailability.querySelector('[data-pickup-availaibility]')
      : null;
    const pickupLocationsAccordion = pickupLocationsList.closest('[data-pickup-locations-accordion]');

    const variantId = pickupLocationsList.dataset.variantId;
    const country = pickupLocationsList.dataset.shippingCountry;
    const province = pickupLocationsList.dataset.shippingProvince;
    const zip = pickupLocationsList.dataset.shippingZip;
    const shippingAvailability = pickupLocationsList.dataset.shippingAvailability;

    if (!variantId || !country || !province || !zip) continue;

    cartCookie = getCookie('cart');
    const tempCookieValue = 'temp-cart-cookie___' + Date.now();
    const fakeCookieValue = 'fake-cart-cookie___' + Date.now();

    /* if not found, make a new temp cookie */
    if (!cartCookie) {
      updateCartCookie(tempCookieValue);
      cartCookie = getCookie('cart');
    }

    /* if found but has a weird length, bail */
    if (cartCookie.length < 32) {
      continue;
    }

    /* Change the cookie value to a new 32 character value */
    updateCartCookie(fakeCookieValue);

    await getRates(
      variantId,
      country,
      province,
      zip,
      pickupLocationsList,
      pickupLocationsAccordion,
      pickupAvailability,
      shippingAvailability
    );
  }

  /* get cookie by name */
  function getCookie(name) {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length == 2) return parts.pop().split(';').shift();
  }

  /* update the cart cookie value */
  function updateCartCookie(a) {
    const date = new Date();
    date.setTime(date.getTime() + 14 * 86400000);
    const expires = '; expires=' + date.toGMTString();
    document.cookie = 'cart=' + a + expires + '; path=/';
  }

  /* reset the cart cookie value */
  function resetCartCookie() {
    updateCartCookie(cartCookie);
  }

  /* get the rates */
  function getRates(
    variantId,
    country,
    province,
    zip,
    pickupLocationsList,
    pickupLocationsAccordion,
    pickupAvailability,
    shippingAvailability
  ) {
    return new Promise((resolve, reject) => {
      const addData = {
        id: variantId,
        quantity: 1,
      };

      $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        data: addData,
        dataType: 'json',
        success: () => {
          $.ajax({
            type: 'GET',
            url: '/cart/shipping_rates.json',
            data: {
              'shipping_address[country]': country,
              'shipping_address[province]': province,
              'shipping_address[zip]': zip,
            },
            dataType: 'json',
            success: (data) => {
              resetCartCookie();
              if (!data || !data.shipping_rates) return resolve(false);

              const listType = pickupLocationsList.dataset.pickupLocationsList || 'available';

              const pickupLocations = data.shipping_rates.filter((rate) => rate.name.startsWith(PICKUP_PREFIX));
              if (pickupLocations.length === 0) {
                if (pickupAvailability) {
                  pickupAvailability.innerHTML =
                    '<span class="pull-right" style="color: #d01012;">Not available</span>';
                }

                if (pickupLocationsAccordion) {
                  pickupLocationsAccordion.style.display = 'none';
                }

                if (shippingAvailability === 'unavailable') {
                  const addToCartButtons = document.querySelectorAll('[data-action="add-to-cart"]');
                  addToCartButtons.forEach((addToCartButton) => (addToCartButton.disabled = true));
                }

                if (listType === 'unavailable') {
                  const allLocationsList =
                    window.Shopify?.currency?.active === 'NZD' ? NZ_PICKUP_LOCATIONS : AU_PICKUP_LOCATIONS;
                  pickupLocationsList.innerHTML = window.languages.giftWithPurchaseNotAvailableAt.replace(
                    '{{ locations }}',
                    commaSeparateWithAnd(allLocationsList)
                  );
                }

                return resolve(true);
              }

              if (pickupAvailability) {
                pickupAvailability.innerHTML = '<span class="pull-right">Available</span>';
              }

              if (listType === 'unavailable') {
                const allLocationsList =
                  window.Shopify?.currency?.active === 'NZD' ? NZ_PICKUP_LOCATIONS : AU_PICKUP_LOCATIONS;
                const listItems = allLocationsList.filter(
                  (location) => !pickupLocations.some((rate) => rate.name === `${PICKUP_PREFIX}${location}`)
                );

                if (listItems.length > 0) {
                  pickupLocationsList.innerHTML = window.languages.giftWithPurchaseNotAvailableAt.replace(
                    '{{ locations }}',
                    commaSeparateWithAnd(listItems)
                  );
                }
              } else {
                const listItems = pickupLocations.map(
                  (rate) => `
                    <li class="store-availability-list__item">
                      ${window.theme?.icons?.inStock}
                      ${rate.name.replace(PICKUP_PREFIX, '')}
                    </li>
                  `
                );
                if (listItems.length > 0) {
                  pickupLocationsList.innerHTML = `<ul class="store-availabilities-list" role="list">${listItems.join(
                    ''
                  )}</ul>`;
                }
              }

              resolve(true);
            },
            error: (err) => {
              resetCartCookie();

              if (pickupAvailability) {
                pickupAvailability.innerHTML = '<span class="pull-right" style="color: #d01012;">Not available</span>';
              }

              if (pickupLocationsAccordion) {
                pickupLocationsAccordion.style.display = 'none';
              }

              resolve(false);
            },
          });
        },
        error: (err) => {
          resetCartCookie();

          resolve(false);
        },
      });
    });
  }
});

/**
 * Quantity limits
 */
document.addEventListener('DOMContentLoaded', function () {
  // Initialise PDP quantity limit messaging toggles
  const quantityLimitInfo = document.querySelector('[data-qty-limit-info]');
  if (quantityLimitInfo) {
    function showQuantityLimit() {
      quantityLimitInfo.classList.add('is-open');
    }

    function hideQuantityLimit() {
      quantityLimitInfo.classList.remove('is-open');
    }

    function toggleQuantityLimit() {
      quantityLimitInfo.classList.toggle('is-open');
    }

    const quantityLimitToggles = document.querySelectorAll('[data-toggle-qty-limit-info]');
    quantityLimitToggles.forEach(function (quantityLimitToggle) {
      quantityLimitToggle.addEventListener('click', toggleQuantityLimit);
      if (!document.documentElement.classList.contains('supports-hover')) return;

      quantityLimitToggle.addEventListener('mouseenter', showQuantityLimit);
      quantityLimitToggle.addEventListener('mouseleave', hideQuantityLimit);
    });
  }

  // If any products in the cart are over the max quantity update them to the max
  const itemsToUpdate = {};
  const quantityInputs = document.querySelectorAll('[name="updates[]"]');
  quantityInputs.forEach(function (quantityInput) {
    const key = quantityInput.dataset.lineId;
    const quantity = Number(quantityInput.value);
    const quantityLimit = Number(quantityInput.max);
    if (quantityLimit > 0 && quantity > quantityLimit) {
      itemsToUpdate[key] = quantityLimit;
    }
  });

  function updateLineItems(updates) {
    const data = {
      updates: updates,
    };
    const url = '/cart/update.js';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    fetch(url, options)
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        if (window.location.pathname === '/cart') {
          window.location.reload();
        }
      })
      .catch(function (error) {
        window.console.error(error);
      });
  }

  if (Object.keys(itemsToUpdate).length > 0) {
    updateLineItems(itemsToUpdate);
  }

  // Cart page - handle discount code
  if (window.location.pathname === '/cart') {
    const discountCodeForm = document.querySelector('[data-cart-discount-code-form]');
    const discountCodeButton = discountCodeForm?.querySelector('[data-discount-code-apply]');
    const discountCodeInput = discountCodeForm?.querySelector('[name="discount"]');

    if (discountCodeButton && discountCodeInput) {
      const applyDiscountCode = (event) => {
        event.preventDefault();
        const discountCode = discountCodeInput.value;

        if (!discountCode) return;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({discount: discountCode}),
        })
          .then((response) => response.json())
          .then((data) => {
            const discountApplied = data?.discount_codes.some((code) => code.code === discountCode && code.applicable);

            if (discountApplied) {
              window.location.reload();
            } else {
              const prefixesInvalidWithBundles = window.theme?.discountCodes?.prefixesInvalidWithBundles.split(',');
              const bundleRejectionMessage = window.theme?.discountCodes?.bundleRejectionMessage;
              const isInvalidWithBundles = prefixesInvalidWithBundles?.some((prefix) =>
                discountCode.startsWith(prefix)
              );
              let discountCodeError = discountCodeForm.parentElement.querySelector('.Cart__DiscountCodeError');
              if (discountCodeError) {
                if (isInvalidWithBundles) {
                  discountCodeError.innerHTML = bundleRejectionMessage;
                } else {
                  discountCodeError.innerHTML = window.languages?.cartDiscountCodeError || 'Discount code not valid';
                }
                discountCodeError.classList.remove('hide');
              } else {
                discountCodeError = document.createElement('p');
                discountCodeError.classList.add('Cart__DiscountCodeError', 'Form__Alert', 'Alert', 'Alert--error');
                if (isInvalidWithBundles) {
                  discountCodeError.innerHTML = bundleRejectionMessage;
                } else {
                  discountCodeError.innerHTML = window.languages?.cartDiscountCodeError || 'Discount code not valid';
                }
                discountCodeForm.insertAdjacentElement('afterend', discountCodeError);
              }
            }
          })
          .catch(function (error) {
            window.console.error('LEGO | Discount code error:', error);
          });
      };

      discountCodeButton.addEventListener('click', applyDiscountCode);
      discountCodeInput.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          applyDiscountCode(event);
        }
      });
    }
  }
});

/**
 * Accordions
 */
document.addEventListener('DOMContentLoaded', function () {
  document.body.addEventListener('click', (event) => {
    const button = event.target.closest('[data-accordion-trigger]');
    if (!button) return;

    const panel = document.querySelector(`[data-accordion-panel="${button.dataset.accordionTrigger}"]`);
    if (!panel) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', !isExpanded);
    panel.hidden = isExpanded;
  });
});

/**
 * Engraving
 */
document.addEventListener('DOMContentLoaded', function () {
  const engravingCheckbox = document.querySelector('#add-product-engraving');
  const engravingInput = document.querySelector('#product-engraving-text');
  const engravingError = document.querySelector('[data-engraving-error]');
  const engravingQuantity = document.querySelector('[data-engraving-quantity]');
  const engravingFields = document.querySelectorAll('[data-engraving-field]');
  const engravingIds = document.querySelectorAll('[data-engraving-id]');

  if (!engravingCheckbox || !engravingInput || !engravingQuantity || !engravingError) return;

  const productForm = engravingInput.closest('form');
  if (!productForm) return;

  const quantityInput = productForm.querySelector('[name="quantity"]');
  if (!quantityInput) return;

  const submitButtons = productForm.querySelectorAll('[type="submit"]');

  /**
   * If add an engraving checkbox is not ticked disable all the engraving fields,
   * otherwise enabled them so the user can enter their desired text.
   */
  const updateEngravingFields = () => {
    engravingFields.forEach((engravingField) => (engravingField.disabled = !engravingCheckbox.checked));
    if (engravingCheckbox.checked) {
      engravingInput.focus();
    }
  };
  engravingCheckbox.addEventListener('change', updateEngravingFields);
  window.addEventListener('load', updateEngravingFields);

  /**
   * Only allow the user to enter letters or numbers in the engraving text input.
   * Show error message and disable submit if their engraving contains any words in the profanity list.
   */
  const letterOrNumber = /^[0-9a-zA-Z]+$/;
  const profanityListElement = document.querySelector('[data-engraving-profanity-list]');
  const profanityList = profanityListElement ? JSON.parse(profanityListElement.innerHTML).profanityList : [];

  engravingInput.addEventListener('input', (event) => {
    const {value} = event.target;
    let newValue = '';

    for (const letter of value) {
      if (letter.match(letterOrNumber)) {
        newValue += newValue.length > 0 ? letter.toLowerCase() : letter;
      }
    }

    const containsProfanity = profanityList.some((word) => newValue.toLowerCase().includes(word));
    engravingError.hidden = !containsProfanity;
    submitButtons.forEach((submitButton) => (submitButton.disabled = containsProfanity));

    const timestamp = Date.now();
    engravingIds.forEach((engravingId) => (engravingId.value = timestamp));

    event.target.value = newValue;
  });

  /**
   * Keep engraving quantity in sync with the main product quantity
   */
  const updateEngravingQuantity = () => {
    engravingQuantity.value = quantityInput.value;
  };
  quantityInput.addEventListener('input', updateEngravingQuantity);
  window.addEventListener('load', updateEngravingQuantity);
});

/**
 * Product features mutation observer
 *
 * On the PDP product features content gets added dynamically from Flixmedia.
 * We hide this section by default if no custom content is defined and only
 * show it when the content from Flixmedia is added to the page.
 */
document.addEventListener('DOMContentLoaded', function () {
  const productFeatures = document.querySelector('#productFeatures');
  if (!productFeatures) return;
  const flixInpage = productFeatures.querySelector('#flix-inpage');
  if (!flixInpage) return;

  const callback = function (_, observer) {
    if (flixInpage.textContent) {
      productFeatures.style.display = 'block';
    }
    observer.disconnect();
  };

  const observer = new MutationObserver(callback);
  observer.observe(flixInpage, {childList: true, subtree: true});
});

function initMutationObserver(section) {
  const sliderElem = section.querySelector('.flickity-enabled');
  if (sliderElem) {
    const flkty = Flickity.data(sliderElem);
    flkty.resize();
  }
  const observerOptions = {childList: true};
  let observer = new MutationObserver(function () {
    observer.disconnect();
  });
  observer.observe(section, observerOptions);
}

window.addEventListener('recentlyViewed:loaded', () => {
  const relatedViewedProducts = document.querySelector('.recently-viewed-products');
  if (relatedViewedProducts) {
    MatchHeight.init();
    initMutationObserver(relatedViewedProducts);
  }
});

document.addEventListener('DOMContentLoaded', function () {
  const productRecommendations = document.querySelector('.ProductRecommendations');
  if (productRecommendations) {
    MatchHeight.init();
    initMutationObserver(productRecommendations);
  }
});

/**
 * Loyalty
 */
document.addEventListener('DOMContentLoaded', function () {
  const loyaltyBalanceElements = document.querySelectorAll('[data-loyalty-balance]');

  if (loyaltyBalanceElements.length === 0) {
    return;
  }

  initLoyalty();
});

/* Tagalys Search config init */
document.addEventListener('DOMContentLoaded', function () {
  configureTagalys();
  searchSuggestionsTagalys();
  load(['tagalys-smart-widget', 'tagalys-recommendations', 'tagalys-bought-also-bought', 'tagalys-viewed-also-viewed']);
});

/* Featured Column Content section */
document.addEventListener('DOMContentLoaded', function () {
  const initFeaturedColumnContent = (section) => {
    const carousel = section.querySelector('.SectionFeaturedColumnContent__MobileCarousel');
    if (!carousel) return;

    let featuredColumnContentSlider = new Flickity(carousel, {
      prevNextButtons: false,
      wrapAround: false,
      pageDots: carousel.dataset?.carouselPagination === 'dots',
      autoPlay: false,
      cellAlign: 'left',
      percentPosition: false,
      watchCSS: true,
      contain: true,
    });

    const progressIndicator = section.querySelector('.SectionFeaturedColumnContent__ProgressIndicator');
    if (!progressIndicator) return;

    featuredColumnContentSlider.on('scroll', function (progress) {
      const leftPosition = progress * (100 - 100 / featuredColumnContentSlider.cells.length);
      progressIndicator.style.setProperty('left', `${parseFloat(leftPosition).toFixed(2)}%`);
    });
  };

  const featuredColumnContentSections = document.querySelectorAll('[data-section-type="featured-column-content"]');

  if (featuredColumnContentSections.length > 0) {
    featuredColumnContentSections.forEach((section) => {
      initFeaturedColumnContent(section);
    });
  }
});

/* Featured Posts Slider with Custom Navigation */
document.addEventListener('DOMContentLoaded', function () {
  const initFeaturedPostsSlider = (section) => {
    const articleList = section.querySelector('[data-article-list]');
    if (!articleList) return;

    const flickity = new Flickity(articleList, {
      prevNextButtons: false,
      pageDots: false,
      cellAlign: 'left',
      contain: true,
      watchCSS: true,
    });

    const prevButton = section.querySelector('[data-slider-prev]');
    const nextButton = section.querySelector('[data-slider-next]');

    prevButton?.addEventListener('click', () => {
      flickity.previous();
    });

    nextButton?.addEventListener('click', () => {
      flickity.next();
    });

    flickity?.on('select', () => {
      const flickityWidth = flickity.size.width;
      const cellWidth = flickity.cells[0]?.size?.outerWidth ?? 0;
      const visibleCount = cellWidth > 0 ? Math.floor(flickityWidth / cellWidth) : 1;

      prevButton.disabled = flickity.selectedIndex === 0;
      nextButton.disabled = flickity.selectedIndex === flickity.cells.length - visibleCount;
    });
  };

  const featuredPostsSection = document.querySelectorAll('[data-section-type="featured-posts"]');

  if (featuredPostsSection.length > 0) {
    featuredPostsSection.forEach((section) => {
      initFeaturedPostsSlider(section);
    });
  }
});

/* Featured Collections Slider Custom Navigation */
document.addEventListener('DOMContentLoaded', function () {
  const initFeaturedCollectionsSliderNav = (section) => {
    const featuredCollectionsSlider = section.querySelector('[data-flickity-config]');
    if (!featuredCollectionsSlider) return;

    const flickity = Flickity.data(featuredCollectionsSlider);

    if (!flickity) return;

    window.addEventListener('load', () => {
      flickity.resize();
    });

    const prevButton = section.querySelector('[data-slider-prev]');
    const nextButton = section.querySelector('[data-slider-next]');

    if (!prevButton || !nextButton) return;

    prevButton.addEventListener('click', () => {
      flickity.previous();
    });

    nextButton.addEventListener('click', () => {
      flickity.next();
    });

    flickity.on('select', () => {
      prevButton.disabled = flickity.selectedIndex === 0;
      nextButton.disabled = flickity.selectedIndex === flickity.slides.length - 1;
    });
  };

  const featuredCollectionsSections = document.querySelectorAll('[data-section-type="featured-collections"]');

  if (featuredCollectionsSections.length > 0) {
    featuredCollectionsSections.forEach((section) => {
      initFeaturedCollectionsSliderNav(section);
    });
  }
});

/* Subcategory Links section - added to collection page */
document.addEventListener('DOMContentLoaded', function () {
  const subcategoryLinksSection = document.querySelector('[data-subcategory-links]');
  if (!subcategoryLinksSection) return;

  const carousel = subcategoryLinksSection.querySelector('[data-slider]');
  if (!carousel) return;

  const progressBarWrapper = subcategoryLinksSection.querySelector('[data-slider-progress-wrapper]');
  const progressIndicator = progressBarWrapper?.querySelector('[data-slider-progress-indicator]');
  const progressIndicatorWidth = parseFloat(getComputedStyle(progressIndicator).getPropertyValue('--width')) || 0;
  const progressIndicatorMaxLeft = Math.max(0, 100 - progressIndicatorWidth);

  let sliderInstance = null;

  const initSlider = () => {
    if (sliderInstance) return;

    sliderInstance = new Flickity(carousel, {
      prevNextButtons: false,
      freeScroll: false,
      wrapAround: false,
      pageDots: false,
      autoPlay: false,
      cellAlign: 'left',
      percentPosition: false,
      contain: true,
    });

    progressBarWrapper?.classList.remove('is-hidden');

    if (!progressIndicator) return;

    sliderInstance.on('scroll', function (progress) {
      const leftPosition = Math.max(
        0,
        Math.min(progressIndicatorMaxLeft, progress * (100 - 100 / sliderInstance.cells.length))
      );
      progressIndicator.style.setProperty('left', `${leftPosition.toFixed(2)}%`);
    });
  };

  const destroySlider = () => {
    if (!sliderInstance) return;

    sliderInstance.destroy();
    sliderInstance = null;

    progressBarWrapper?.classList.add('is-hidden');
  };

  if (carousel.offsetWidth < carousel.scrollWidth) {
    initSlider();
  }

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      if (sliderInstance) {
        if (sliderInstance.slideableWidth <= sliderInstance.size.width) {
          destroySlider();
        }
      } else if (carousel.scrollWidth > carousel.clientWidth) {
        initSlider();
      }
    }, 150);
  });
});

/*Shop the Look Gallery Section */
document.addEventListener('DOMContentLoaded', function () {
  const initShopTheLookDialogs = (section) => {
    const shopTheLookDialogs = section.querySelectorAll('dialog');
    const shopTheLookDialogsArray = Array.from(shopTheLookDialogs);
    const dialogOpenButtons = section.querySelectorAll('[data-shop-the-look-gallery-trigger]');
    const dialogCloseButtons = section.querySelectorAll('[data-shop-the-look-gallery-close]');

    // Initialize all dialogs once - calculate the height of the dialog content
    shopTheLookDialogs?.forEach((dialog) => {
      dialog.style.setProperty('--height-expanded', dialog.scrollHeight + 'px');
      dialog.close();
      dialog.classList.remove('ShopTheLookGallery__Dialog--init');
    });

    const closeShopTheLookDialog = (dialog) => {
      dialog.classList.remove('ShopTheLookGallery__Dialog--expanded');

      dialog.addEventListener(
        'transitionend',
        () => {
          dialog.close();
        },
        {once: true}
      );
    };

    // Dialog open buttons
    dialogOpenButtons?.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        const openDialog = shopTheLookDialogsArray.find((dialog) => dialog.open);
        if (openDialog) {
          closeShopTheLookDialog(openDialog);
        }

        const dialog = section.querySelector(`#${button.getAttribute('aria-controls')}`);
        if (!dialog) return;

        dialog.show();

        requestAnimationFrame(() => {
          dialog.classList.add('ShopTheLookGallery__Dialog--expanded');
        });
      });
    });

    // Dialog close buttons
    dialogCloseButtons?.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        const dialog = section.querySelector(`#${button.getAttribute('aria-controls')}`);
        if (!dialog) return;

        closeShopTheLookDialog(dialog);
      });
    });
  };

  const shopTheLookGallerySections = document.querySelectorAll('[data-section-type="shop-the-look-gallery"]');

  if (shopTheLookGallerySections.length > 0) {
    shopTheLookGallerySections.forEach((section) => {
      initShopTheLookDialogs(section);
    });
  }
});

// Replace LazySizes animate on scroll with IntersectionObserver
function lazySizesAnimateOnScroll() {
  const lazySizes = document.querySelectorAll('.Image--lazyLoad');
  if (lazySizes.length === 0) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      setTimeout(() => {
        entry.target.classList.add('Image--lazyLoaded');
      }, 300);
    });
  });

  lazySizes.forEach((lazySize) => {
    observer.observe(lazySize);
  });
}

document.addEventListener('DOMContentLoaded', lazySizesAnimateOnScroll);

// Countdown Timer Bar
class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.settings = JSON.parse(this.dataset.settings);

    this.clockElements = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };

    this.eventDate = new Date(this.settings[this.settings.state ?? 'primary'].date);

    this.updateGetTime(this.eventDate);
    this.updateClock = setInterval(this.updateGetTime.bind(this), 1000);
  }

  validateTime(distance) {
    if (distance > 0 && !isNaN(distance)) return;

    // Live banner update
    // Handle time switch if secondary event is enabled and
    // the primary event has ended after the page has loaded.
    if (this.settings.secondary.enabled && this.settings.state !== 'secondary') {
      const secondaryEventDate = new Date(this.settings.secondary.date);
      if (secondaryEventDate !== this.eventDate) {
        this.eventDate = secondaryEventDate;
        this.settings.state = 'secondary';
        this.updateText(this.settings.secondary);
      }
      return;
    }

    // Stop timer if time is invalid
    clearInterval(this.updateClock);
  }

  updateGetTime() {
    // Convert current time to AEST
    const now = new Date(new Date().toLocaleString('en-US', {timeZone: 'Australia/Sydney'}));

    // Calculate the distance between now and the event date
    const nowTime = new Date(now).getTime();
    const event = new Date(this.eventDate).getTime();
    const distance = event - nowTime;

    this.validateTime(distance);

    const time = {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((distance / (1000 * 60)) % 60),
      seconds: Math.floor((distance / 1000) % 60),
    };

    this.updateClockEl(time);
  }

  updateClockEl(time) {
    Object.keys(time).forEach((key) => {
      const number = time[key] <= 0 || isNaN(time[key]) ? '00' : time[key].toString().padStart(2, '0');
      this.clockElements[key].innerHTML = `
        <span>${number[0]}</span>
        <span>${number[1]}</span>
        <div class="countdown-timer__number-label">${key}</div>
      `;
    });
  }

  updateText(data) {
    const textEl = this.querySelector('[data-text]');
    if (textEl && (data.title || data.text)) {
      textEl.innerHTML = `
        ${data.title ? `<h2>${data.title}</h2>` : ''}
        ${data.text ? `<p>${data.text}</p>` : ''}`;
    }

    const ctaEl = this.querySelector('[data-cta]');
    if (ctaEl && data.ctaText && data.ctaLink) {
      ctaEl.innerHTML = `
        <a href="${data.ctaLink}" class="Button Button--primary">
          ${data.ctaText}
        </a>`;
    }
  }
}

function initCountdownTimer() {
  customElements.define('countdown-timer', CountdownTimer);
}

document.addEventListener('DOMContentLoaded', initCountdownTimer);

/* Gift with Purchase */
document.addEventListener('DOMContentLoaded', async () => {
  // Fall back for cart loader
  const cartLoader = document.querySelector('[data-cart-loader]');
  if (cartLoader) {
    setTimeout(() => {
      cartLoader.classList.add('hidden');
    }, 1000);
  }

  await initGiftWithPurchase();
  initGiftCardLanding();
});

/* Main Customer Register */

const initMainCustomersRegister = () => {
  const mainCustomerRegisterSection = document.querySelector('.main-customers-register');
  if (!mainCustomerRegisterSection) return;

  const selectors = {
    phoneInput: 'input[type="tel"]',
    submit: '.Form__Submit',
    phoneInputError: '.Form__Item-Error',
  };

  const classes = {
    errorActive: 'Form__Item-Error--active',
  };

  const phoneInput = mainCustomerRegisterSection.querySelector(selectors.phoneInput);
  const phoneInputError = mainCustomerRegisterSection.querySelector(selectors.phoneInputError);
  const formSubmit = mainCustomerRegisterSection.querySelector(selectors.submit);

  function isValidPhoneNumber(phoneNumber) {
    if (!phoneNumber) return true;

    const phoneRegex = /^[\d()+-]+$/;
    return phoneRegex.test(phoneNumber);
  }

  function formIsValid() {
    formSubmit.removeAttribute('disabled');
    phoneInputError.classList.remove(classes.errorActive);
  }

  function formHasErrors() {
    formSubmit.setAttribute('disabled', true);
    phoneInputError.classList.add(classes.errorActive);
  }

  if (!phoneInput || !formSubmit || !phoneInputError) return;

  phoneInput.addEventListener('change', (e) => {
    isValidPhoneNumber(e.target.value) ? formIsValid() : formHasErrors();
  });
};
document.addEventListener('DOMContentLoaded', initMainCustomersRegister());

// Handle ajax add to cart confirmation modal
document.addEventListener('DOMContentLoaded', function () {
  if (!window.theme?.cartEnableConfirmationPopup) return;

  const cartConfirmationElem = document.querySelector('cart-confirmation');

  const cartConfirmationModal = cartConfirmationElem?.querySelector('#modal-cart-confirmation');
  const cartConfirmationTrigger = cartConfirmationElem?.querySelector('[data-cart-confirmation-trigger]');
  const cartConfirmationClose = cartConfirmationElem?.querySelector('[data-action="close-modal"]');
  const cartCountElem = document.querySelectorAll('[data-cart-count]');
  class CartConfirmation extends HTMLElement {
    connectedCallback() {
      this.selectors = {
        title: '[data-product-title]',
        image: '[data-product-image]',
        price: '[data-product-price]',
        quantity: '[data-product-quantity]',
      };
      this.titleElem = this.querySelector(this.selectors.title);
      this.imageElem = this.querySelector(this.selectors.image);
      this.priceElem = this.querySelector(this.selectors.price);
      this.quantityElem = this.querySelector(this.selectors.quantity);
    }

    updateProduct(item) {
      if (this.titleElem) this.titleElem.textContent = item.title;

      if (this.imageElem) {
        const src = imageUrl(item.image.url, 320);
        this.imageElem.src = src;
        this.imageElem.alt = item.image.alt || item.title;
      }

      if (this.priceElem) this.priceElem.textContent = formatMoney(item.price, window.theme?.moneyWithCurrencyFormat);

      if (this.quantityElem) this.quantityElem.textContent = item.quantity;
    }
  }

  customElements.define('cart-confirmation', CartConfirmation);

  let cartConfirmationHideTimer;

  cartConfirmationModal?.addEventListener('mouseenter', () => {
    clearTimeout(cartConfirmationHideTimer);
  });

  cartConfirmationModal?.addEventListener('mouseleave', () => {
    cartConfirmationHideTimer = setTimeout(() => {
      if (cartConfirmationModal?.getAttribute('aria-hidden') === 'false') {
        cartConfirmationClose?.click();
      }
    }, 2000);
  });

  // FORM submit -> add to cart handler
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      // Only handle ProductForm submissions
      if (!form?.hasAttribute('data-product-form')) return;

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = window.languages.productFormAddingToCart;
      }

      event.preventDefault();

      const formData = new FormData(form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const data = await response.json();

        const customItem = {
          title: data.title ?? '',
          image: data.featured_image ?? '',
          price: data.price ?? '',
          quantity: data.quantity ?? '',
        };

        cartConfirmationElem?.updateProduct(customItem);

        cartConfirmationTrigger?.click();

        cartConfirmationHideTimer = setTimeout(() => {
          if (cartConfirmationModal?.getAttribute('aria-hidden') === 'false') {
            cartConfirmationClose?.click();
          }
        }, 3000);

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = window.languages.productFormAddToCart;
        }

        if (cartCountElem.length > 0) {
          const oldCartCount = parseInt(cartCountElem[0].dataset?.cartCount ?? 0) || 0;
          const newCartCount = oldCartCount + data.quantity;
          cartCountElem.forEach((elem) => {
            elem.dataset.cartCount = newCartCount;
            elem.textContent = newCartCount;
          });
        }
      } catch (error) {
        console.error('Error:', error);

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = window.languages.productFormAddToCart;

          const errorMessage = document.createElement('span');
          errorMessage.className = 'ProductForm__Error Alert Alert--error';
          errorMessage.textContent = error.message;
          submitButton.insertAdjacentElement('afterend', errorMessage);

          setTimeout(() => errorMessage.remove(), 2500);
        }
      }
    },
    true
  );
});
